import { Component, HostListener, inject, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../services/auth.service';
import { PageProcessService } from '../../services/page-process.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatBadgeModule,
    MatTooltipModule
  ],
  template: `
    <mat-sidenav-container class="sidenav-container">
      <mat-sidenav #sidenav [mode]="isMobile ? 'over' : 'side'" [opened]="!isMobile" class="sidenav">
        <div class="sidenav-header">
          <div class="logo">
            <img src="/logo.png" alt="SecAudit" class="logo-img" />
            <span>SecAudit</span>
          </div>
        </div>

        <nav class="nav-list">
          <ng-container *ngIf="isEmpresa">
            <div class="nav-section">Principal</div>
            <a class="nav-item" routerLink="/dashboard" routerLinkActive="active">
              <mat-icon>dashboard</mat-icon>
              <span>Dashboard</span>
            </a>
            <a class="nav-item" routerLink="/pages" routerLinkActive="active">
              <mat-icon>language</mat-icon>
              <span>Páginas Web</span>
            </a>
            <a class="nav-item" routerLink="/processes" routerLinkActive="active">
              <mat-icon>assignment</mat-icon>
              <span>Procesos</span>
            </a>
          </ng-container>

          <ng-container *ngIf="isAdmin">
            <div class="nav-section">Administración</div>
            <a class="nav-item" routerLink="/users" routerLinkActive="active">
              <mat-icon>people</mat-icon>
              <span>Usuarios</span>
            </a>
            <a class="nav-item" routerLink="/roles" routerLinkActive="active">
              <mat-icon>admin_panel_settings</mat-icon>
              <span>Roles</span>
            </a>
            <a class="nav-item" routerLink="/permissions" routerLinkActive="active">
              <mat-icon>vpn_key</mat-icon>
              <span>Permisos</span>
            </a>
            <a class="nav-item" routerLink="/cover-pages" routerLinkActive="active">
              <mat-icon>image</mat-icon>
              <span>Portadas</span>
            </a>
          </ng-container>
        </nav>
      </mat-sidenav>

      <mat-sidenav-content>
        <mat-toolbar class="app-toolbar">
          <button mat-icon-button (click)="sidenav.toggle()">
            <mat-icon>menu</mat-icon>
          </button>

          <span class="toolbar-spacer"></span>

          <button mat-icon-button *ngIf="isEmpresa" [matBadge]="failedCount" matBadgeColor="warn" matBadgeSize="small"
                  (click)="goToProcesses()" matTooltip="Procesos fallidos">
            <mat-icon>notifications</mat-icon>
          </button>

          <button mat-button [matMenuTriggerFor]="userMenu" class="user-menu">
            <div class="user-avatar">{{ getUserInitials() }}</div>
            <span>{{ currentUser?.username || 'Usuario' }}</span>
            <mat-icon>arrow_drop_down</mat-icon>
          </button>

          <mat-menu #userMenu="matMenu">
            <button mat-menu-item>
              <mat-icon>person</mat-icon>
              <span>Mi Perfil</span>
            </button>
            <button mat-menu-item>
              <mat-icon>settings</mat-icon>
              <span>Configuración</span>
            </button>
            <mat-divider></mat-divider>
            <button mat-menu-item (click)="logout()">
              <mat-icon>logout</mat-icon>
              <span>Cerrar Sesión</span>
            </button>
          </mat-menu>
        </mat-toolbar>

        <main class="main-content">
          <router-outlet></router-outlet>
        </main>
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  styles: [`
    .sidenav-container {
      height: 100vh;
    }

    .sidenav {
      width: 260px;
      background: var(--surface-dark);
      border-right: 1px solid var(--border-dark);
    }

    .sidenav-header {
      padding: 22px 20px;
      border-bottom: 1px solid var(--border-dark);
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 12px;

      .logo-img {
        width: 30px;
        height: 30px;
        border-radius: 8px;
        object-fit: cover;
        box-shadow: 0 0 24px rgba(183, 28, 28, 0.3);
      }

      span {
        font-family: var(--font-display);
        font-size: 18px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-on-dark);
      }
    }

    .nav-list {
      padding: 16px 12px;
    }

    .nav-section {
      padding: 16px 12px 8px;
      font-size: 11px;
      font-weight: 600;
      color: var(--text-muted-on-dark);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      opacity: 0.75;
    }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      margin-bottom: 4px;
      border-radius: 12px;
      color: var(--text-muted-on-dark);
      text-decoration: none;
      transition: background 0.2s ease, color 0.2s ease;
      cursor: pointer;

      &:hover {
        background: rgba(255, 255, 255, 0.05);
        color: var(--text-on-dark);
      }

      &.active {
        background: rgba(183, 28, 28, 0.18);
        color: var(--text-soft-on-dark);

        mat-icon {
          color: var(--primary-light);
        }
      }

      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
      }

      span {
        font-size: 14px;
        font-weight: 500;
      }
    }

    .app-toolbar {
      background: var(--surface-dark-2) !important;
      color: var(--text-on-dark) !important;
      border-bottom: 1px solid var(--border-dark);
      position: sticky;
      top: 0;
      z-index: 100;

      --mdc-icon-button-icon-color: var(--text-on-dark);
      --mdc-icon-button-icon-state-color: var(--text-on-dark);
      --mdc-icon-button-state-layer-color: rgba(255, 255, 255, 0.08);
      --mat-icon-button-icon-color: var(--text-on-dark);
      --mat-icon-button-icon-state-color: var(--text-on-dark);
      --mdc-text-button-label-text-color: var(--text-on-dark);
      --mdc-text-button-label-text-state-color: var(--text-on-dark);
      --mdc-text-button-state-layer-color: rgba(255, 255, 255, 0.08);
      --mat-text-button-label-text-color: var(--text-on-dark);
    }

    .toolbar-spacer {
      flex: 1 1 auto;
    }

    .app-toolbar .mat-mdc-icon-button,
    .app-toolbar .mat-mdc-button,
    .app-toolbar button,
    .app-toolbar mat-icon {
      color: var(--text-on-dark);
      --mdc-icon-button-icon-color: var(--text-on-dark);
      --mdc-text-button-label-text-color: var(--text-on-dark);
    }

    .user-menu {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: 8px;
      color: var(--text-on-dark);

      mat-icon {
        color: var(--text-on-dark);
      }
    }

    .user-avatar {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      background: linear-gradient(145deg, #b71c1c, #7f0000);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 600;
      box-shadow: 0 2px 10px rgba(183, 28, 28, 0.35);
    }

    .main-content {
      padding: 24px;
      background: #f7f8fa;
      min-height: calc(100vh - 64px);
    }

    @media (max-width: 768px) {
      .main-content {
        padding: 16px;
      }

      .app-toolbar .user-menu span {
        display: none;
      }
    }
  `]
})
export class LayoutComponent implements OnInit, OnDestroy {
  isMobile = window.innerWidth <= 768;
  failedCount = 0;
  private pollSubscription?: Subscription;

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private pageProcessService: PageProcessService
  ) {}

  ngOnInit() {
    if (this.isEmpresa) {
      this.loadFailedCount();
      this.pollSubscription = interval(30000).subscribe(() => this.loadFailedCount());
    }
  }

  ngOnDestroy() {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }
  }

  private loadFailedCount() {
    const now = new Date();
    this.pageProcessService.getProcessesByMonth(now.getMonth() + 1, now.getFullYear()).subscribe({
      next: (processes) => {
        this.failedCount = processes.filter(p => p.status === 4).length;
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  goToProcesses() {
    this.router.navigate(['/processes']);
  }

  @HostListener('window:resize')
  onResize() {
    const nowMobile = window.innerWidth <= 768;
    if (nowMobile !== this.isMobile) {
      this.isMobile = nowMobile;
      this.cdr.detectChanges();
    }
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  get currentUser() {
    return this.authService.getCurrentUser();
  }

  get isAdmin(): boolean {
    return this.authService.getCurrentUserRole() === 'Admin';
  }

  get isEmpresa(): boolean {
    return this.authService.getCurrentUserRole() === 'Empresa';
  }

  getUserInitials(): string {
    const user = this.currentUser;
    if (!user) return 'U';
    return user.username.substring(0, 2).toUpperCase();
  }
}
